import { 
  CreditCard, 
  CheckCircle, 
  Clock, 
  Loader2,
  Link2,
  Building,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../ui/button.tsx';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card.tsx';
import { Badge } from '../ui/badge.tsx';
import { useCheckStatus, useConnectStripeAccount, useStripeDashboardLink } from '../../hooks/usePartnerStripe.ts';
import Loading from './stripe/loading.tsx';
import Error from './stripe/error.tsx';
import InfoItem from './stripe/InfoItem.tsx';
import { usePartner } from '../../hooks/usePartner.ts';

export function StripeConnectTab() {
  const { data: partner, isLoading, error, refetch } = usePartner();
  const statusQuery = useCheckStatus(partner);
  const connectAccountMutation = useConnectStripeAccount();
  const dashboardLinkMutation = useStripeDashboardLink();

  const isStripeConnected = partner?.stripe_connect_account_id;

  if (isLoading) return <Loading />;
  if (error) return <Error error={error} refetch={refetch} />;
  
  return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="size-5" />
                Stripe Account
              </CardTitle>
              <CardDescription>
                Connect your Stripe account to receive commission payouts
              </CardDescription>
            </div>
            {isStripeConnected
              ? <Badge variant="default" className="bg-green-500"><CheckCircle className="size-3 mr-1" />Connected</Badge>
              : <Badge variant="secondary">Not Connected</Badge>
            }
          </div>
        </CardHeader>
        <CardContent>
          {isStripeConnected && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-sm">Account Details</h4>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={async () => { await statusQuery.refetch(); await refetch(); }}
                  disabled={statusQuery.isFetching}
                  className="flex items-center gap-1 h-8"
                >
                  {statusQuery.isFetching ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                  Sync from Stripe
                </Button>
              </div>
              {partner?.stripe_connect_requirements ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                  {partner.stripe_connect_requirements?.country && (
                    <InfoItem title="Country" type={partner.stripe_connect_requirements?.country} />
                  )}
                  {partner.stripe_connect_requirements?.default_currency && (
                    <InfoItem title="Currency" type={partner.stripe_connect_requirements?.default_currency?.toUpperCase()} />
                  )}
                  {partner.stripe_connect_requirements?.business_type && (
                    <InfoItem title="Business Type" type={partner.stripe_connect_requirements?.business_type} />
                  )}
                  {partner.stripe_connect_requirements?.email && (
                    <InfoItem title="Email" type={partner.stripe_connect_requirements?.email} />
                  )}
                </div>
              ) : (
                <div className="p-3 bg-muted/30 rounded-lg border text-sm text-muted-foreground">
                  No details available yet. Use "Sync from Stripe" to refresh.
                </div>
              )}
            </div>
          )}

          {isStripeConnected && (
            <div className="p-4 bg-muted/30 rounded-lg border mb-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-sm">Account Status</h4>
                {partner.stripe_connect_payouts_enabled 
                  ? <Badge variant="default" className="bg-green-500"> <CheckCircle className="size-3 mr-1" /> Ready for Payouts </Badge>
                  : <Badge variant="secondary"> <Clock className="size-3 mr-1" /> Setup in Progress </Badge>
                }
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  {partner.stripe_connect_charges_enabled ?<CheckCircle className="size-4 text-green-500" />:<Clock className="size-4 text-yellow-500" /> }
                  <span className="text-muted-foreground">
                    Charges: {partner.stripe_connect_charges_enabled ? 'Ready' : 'Pending verification'}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  {partner.stripe_connect_payouts_enabled ? <CheckCircle className="size-4 text-green-500" />:<Clock className="size-4 text-yellow-500" />}
                  <span className="text-muted-foreground">
                    Payouts: {partner.stripe_connect_payouts_enabled ? 'Ready' : 'Pending verification'}
                  </span>
                </div>
              </div>
              
              {!partner.stripe_connect_payouts_enabled &&
                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    💡 Complete verification in your Stripe dashboard to enable commission payouts
                  </p>
                </div>
              }
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            {!isStripeConnected ? (
              <Button 
                onClick={() => connectAccountMutation.mutate()}
                disabled={connectAccountMutation.isPending}
                className="flex items-center gap-2 w-full"
              >
                {connectAccountMutation.isPending  ? <Loader2 className="size-4 animate-spin" /> : <Building className="size-4" />}
                Connect Existing Stripe Account
              </Button>
            ) : (
              <Button 
                variant="outline"
                onClick={() => dashboardLinkMutation.mutate()}
                disabled={dashboardLinkMutation.isPending}
                className="flex items-center gap-2"
              >
                {dashboardLinkMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Link2 className="size-4" /> }
                View Stripe Dashboard
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
  );
}